
import React from 'react';
import { ArrowLeft, Shield, Mail, Globe, Clock, ChevronRight } from 'lucide-react';

interface PrivacyPolicyProps {
  onNavigate: (view: 'home' | 'pilot' | 'privacy') => void;
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({ onNavigate }) => {
  return (
    <div className="bg-brandBg min-h-screen text-zodiac font-sans selection:bg-accent selection:text-white">
      {/* Header Section */}
      <section className="pt-40 pb-20 px-6 md:px-12 max-w-5xl mx-auto">
        <button 
          onClick={() => onNavigate('home')}
          className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.4em] text-zodiac/30 hover:text-accent transition-colors mb-12 group"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
          Back to Home
        </button>
        
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-zodiac leading-none mb-6">Privacy Policy</h1>
            <p className="text-xl text-zodiac/40 font-light">Last Updated: January 15, 2026</p>
          </div>
          <div className="flex items-center gap-4 px-6 py-4 bg-white border border-zodiac/5 rounded-2xl shadow-sm">
            <Shield className="w-5 h-5 text-accent" />
            <div className="text-left">
              <p className="text-[10px] font-black uppercase tracking-widest text-zodiac/30 leading-none mb-1">Status</p>
              <p className="text-xs font-bold text-zodiac uppercase tracking-widest">Active Standard v4.2</p>
            </div>
          </div>
        </div>
        
        <div className="w-full h-px bg-zodiac/10 mb-20"></div>

        {/* Introduction */}
        <div className="max-w-4xl space-y-12">
          <div className="prose prose-lg text-zodiac/80 font-light leading-relaxed space-y-8">
            <p>
              Thank you for choosing to be part of our community. At Groflex and our subsidiary and affiliated 
              sites, we are committed to protecting your personal data and respecting your privacy rights. This 
              Privacy Policy ("Policy") explains what personal data is collected and how it is used by Groflex 
              and its subsidiaries and affiliates (collectively, "Company", "we", "us", or "our") when you access 
              or use the Groflex site as well as any other website, mobile site or mobile application related or 
              linked to Groflex (collectively, the "Platform").
            </p>
            <p>
              This Policy and other documents referenced in it apply to all personal data collected through our 
              Platform and any related services, sales, marketing or events.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 py-12 border-y border-zodiac/5">
             <div className="p-8 bg-white rounded-3xl border border-zodiac/5 shadow-sm">
                <Globe className="w-6 h-6 text-accent mb-6" />
                <h3 className="text-lg font-bold mb-4 tracking-tight">Global Operation</h3>
                <p className="text-sm text-zodiac/60 leading-relaxed font-light">
                  Because we operate globally, we apply local rules regarding the use and sale of personal data, 
                  as well as the choice to receive communications from us.
                </p>
             </div>
             <div className="p-8 bg-white rounded-3xl border border-zodiac/5 shadow-sm">
                <Mail className="w-6 h-6 text-accent mb-6" />
                <h3 className="text-lg font-bold mb-4 tracking-tight">Contact Information</h3>
                <p className="text-sm text-zodiac/60 leading-relaxed font-light">
                  If you have any questions or concerns about our collection or use of your information, 
                  please contact us at <a href="mailto:hello@groflex.ai" className="text-accent font-bold hover:underline">hello@groflex.ai</a>.
                </p>
             </div>
          </div>

          {/* Section A */}
          <div className="space-y-8">
            <h2 className="text-3xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">A. WHAT PERSONAL DATA DO WE COLLECT?</h2>
            <div className="space-y-6 text-zodiac/70 font-light leading-relaxed">
              <h3 className="text-xl font-bold text-zodiac">1. Personal data you disclose to us</h3>
              <p>
                We collect personal data that you voluntarily provide to us when registering to use the Platform, 
                expressing an interest in obtaining information about us or our products and services, when 
                participating in activities on the Platform or otherwise contacting us.
              </p>
              <ul className="space-y-4 list-none pl-0">
                <li className="flex gap-4 p-5 bg-white border border-zodiac/5 rounded-2xl group hover:border-accent/30 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-accent/5 flex items-center justify-center shrink-0">
                    <ChevronRight className="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold text-zodiac mb-1">Name and Contact Data</p>
                    <p className="text-sm">We collect your first and last name, email address, mailing address, phone number, and other similar contact data.</p>
                  </div>
                </li>
                <li className="flex gap-4 p-5 bg-white border border-zodiac/5 rounded-2xl group hover:border-accent/30 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-accent/5 flex items-center justify-center shrink-0">
                    <ChevronRight className="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold text-zodiac mb-1">Credentials</p>
                    <p className="text-sm">We collect usernames, passwords, password hints, and similar security information used for authentication and account access.</p>
                  </div>
                </li>
                <li className="flex gap-4 p-5 bg-white border border-zodiac/5 rounded-2xl group hover:border-accent/30 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-accent/5 flex items-center justify-center shrink-0">
                    <ChevronRight className="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold text-zodiac mb-1">Payment Data</p>
                    <p className="text-sm">All payment data is stored by our third-party payment processor. You should review its privacy policies and contact the payment processor directly.</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Section B & C Combined Content */}
          <div className="space-y-8">
            <h2 className="text-3xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">B. DATA AUTOMATICALLY COLLECTED</h2>
            <p className="text-zodiac/70 font-light leading-relaxed">
              We automatically collect certain information when you visit, use or navigate the Platform. This 
              information does not reveal your specific identity (like your name or contact information) but 
              may include device and usage information, such as your IP address, browser and device 
              characteristics, operating system, and language preferences.
            </p>
          </div>

          {/* Section C */}
          <div className="space-y-8">
            <h2 className="text-3xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6">C. HOW DO WE USE YOUR PERSONAL DATA?</h2>
            <div className="grid sm:grid-cols-2 gap-6">
               {[
                 { t: "Account Creation", d: "To facilitate account creation and the logon process." },
                 { t: "Administration", d: "To send administrative information regarding services or policies." },
                 { t: "Fulfillment", d: "To fulfill and manage your orders for services and subscriptions." },
                 { t: "Decision Making", d: "To provide work product generated by automated analysis and AI technologies." }
               ].map((item, i) => (
                 <div key={i} className="p-6 bg-zodiac/[0.02] border border-zodiac/5 rounded-3xl">
                    <p className="font-bold text-zodiac mb-2 tracking-tight uppercase text-xs">{item.t}</p>
                    <p className="text-sm text-zodiac/60 font-medium leading-relaxed">{item.d}</p>
                 </div>
               ))}
            </div>
          </div>

          {/* EEA Notice */}
          <div className="p-10 bg-zodiac rounded-[3rem] text-white overflow-hidden relative group">
            <div className="absolute top-0 right-0 p-12 opacity-[0.03] pointer-events-none group-hover:scale-110 transition-transform duration-[10s]">
              <Shield className="w-64 h-64 text-white" />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-6 tracking-tight">EUROPEAN ECONOMIC AREA RESIDENTS NOTICE</h2>
              <p className="text-white/60 font-light leading-relaxed mb-8 text-lg">
                We offer our Platform and services within the European Union and therefore the General Data 
                Protection Regulation ("GDPR") applies.
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-2">
                   <p className="text-accent font-black uppercase text-[10px] tracking-widest">Your Rights</p>
                   <ul className="text-sm space-y-3 text-white/40">
                     <li>• Right to Erasure (Right to be Forgotten)</li>
                     <li>• Right to Restriction of Processing</li>
                     <li>• Right to Data Portability</li>
                     <li>• Right of Objection & Withdraw Consent</li>
                   </ul>
                </div>
                <div className="space-y-4">
                   <p className="text-accent font-black uppercase text-[10px] tracking-widest">Exercise Rights</p>
                   <p className="text-sm text-white/70 leading-relaxed">
                     Contact our Data Protection Officer (DPO) at <span className="text-white font-bold">hello@groflex.ai</span> to exercise any of your GDPR-granted rights.
                   </p>
                </div>
              </div>
            </div>
          </div>

          {/* U.S. Notice Table */}
          <div className="space-y-8">
             <h2 className="text-3xl font-bold tracking-tight text-zodiac border-l-4 border-accent pl-6 uppercase">U.S. Residents Notice</h2>
             <div className="overflow-x-auto no-scrollbar rounded-3xl border border-zodiac/10 bg-white">
                <table className="w-full text-left border-collapse">
                   <thead>
                      <tr className="bg-zodiac/[0.02] border-b border-zodiac/10">
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zodiac/30">Category</th>
                        <th className="p-6 text-[10px] font-black uppercase tracking-widest text-zodiac/30">Information Collected</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-zodiac/5">
                      {[
                        { cat: "A. Identifiers", data: "Name, mailing address, IP address, email address." },
                        { cat: "B. Customer Records", data: "Name, address, phone number, credit card number." },
                        { cat: "C. Protected Characteristics", data: "Age (40+), race, national origin, citizenship, sex." },
                        { cat: "D. Commercial Information", data: "Records of products or services purchased." },
                        { cat: "F. Network Activity", data: "Browsing history, search history, interaction with site." },
                        { cat: "G. Geolocation Data", data: "Physical location or movements." }
                      ].map((row, i) => (
                        <tr key={i} className="hover:bg-accent/[0.02] transition-colors">
                           <td className="p-6 font-bold text-sm text-zodiac">{row.cat}</td>
                           <td className="p-6 text-sm text-zodiac/60 font-light">{row.data}</td>
                        </tr>
                      ))}
                   </tbody>
                </table>
             </div>
          </div>

          {/* Footer Contact Callout */}
          <div className="pt-20 pb-32">
             <div className="p-12 bg-accent rounded-[3rem] text-white flex flex-col md:flex-row items-center justify-between gap-10 shadow-2xl shadow-accent/20">
                <div className="text-left">
                  <h3 className="text-3xl font-bold mb-4 tracking-tight">Need a Copy?</h3>
                  <p className="text-white/80 font-light">Request a PDF version of our full legal disclosures for your compliance team.</p>
                </div>
                <a 
                  href="mailto:hello@groflex.ai"
                  className="px-10 py-5 bg-zodiac text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:scale-105 transition-executive shadow-xl"
                >
                  Contact DPO
                </a>
             </div>
             
             <div className="mt-16 text-center space-y-4">
                <p className="text-[10px] font-black uppercase tracking-[0.5em] text-zodiac/20">Execution Governance Standard</p>
                <div className="flex justify-center items-center gap-6">
                   <Clock className="w-4 h-4 text-accent/30" />
                   <span className="text-xs font-bold text-zodiac/30 tracking-tight">Document Retention Policy: 7 Years</span>
                </div>
             </div>
          </div>
        </div>
      </section>
    </div>
  );
};
